import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError, API } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash, CaretDown, CaretRight, ArrowUp, ArrowDown, FolderSimplePlus, UploadSimple, DownloadSimple } from "@phosphor-icons/react";

const DAY = 86400000;
const ROW_H = 34;
const HEAD_H = 46;
const PX_DAY = 20;
const parse = (d) => new Date(d + "T00:00:00");
const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (dstr, n) => { const d = parse(dstr); d.setDate(d.getDate() + n); return fmt(d); };

export default function CronogramaGantt({ canManage }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const timelineRef = useRef(null);
  const fileRef = useRef(null);

  const load = async () => {
    try { const { data } = await api.get("/program/tasks"); setTasks(data.tasks || []); }
    catch (e) { toast.error("No se pudo cargar el cronograma"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const rowToId = useMemo(() => Object.fromEntries(tasks.map((t) => [t.row, t.id])), [tasks]);
  const idToRow = useMemo(() => Object.fromEntries(tasks.map((t) => [t.id, t.row])), [tasks]);

  // Ocultar descendientes de grupos colapsados
  const collapsedIds = useMemo(() => new Set(tasks.filter((t) => t.collapsed && t.is_group).map((t) => t.id)), [tasks]);
  const parentMap = useMemo(() => Object.fromEntries(tasks.map((t) => [t.id, t.parent_id])), [tasks]);
  const isHidden = (t) => {
    let p = t.parent_id;
    while (p) { if (collapsedIds.has(p)) return true; p = parentMap[p]; }
    return false;
  };
  const visible = useMemo(() => tasks.filter((t) => !isHidden(t)), [tasks, collapsedIds]);

  // Rango de fechas
  const dated = tasks.filter((t) => t.start_date && t.end_date);
  const min = dated.length ? new Date(Math.min(...dated.map((t) => +parse(t.start_date)))) : new Date();
  const max = dated.length ? new Date(Math.max(...dated.map((t) => +parse(t.end_date)))) : new Date();
  const minMonth = new Date(min.getFullYear(), min.getMonth(), 1);
  const totalDays = Math.max(30, Math.ceil((max - minMonth) / DAY) + 10);
  const timelineW = totalDays * PX_DAY;
  const dayOffset = (dstr) => Math.round((parse(dstr) - minMonth) / DAY);

  const months = useMemo(() => {
    const arr = []; const d = new Date(minMonth);
    while (d <= max) { arr.push(new Date(d)); d.setMonth(d.getMonth() + 1); }
    if (arr.length === 0) arr.push(new Date(minMonth));
    return arr;
  }, [minMonth.getTime(), max.getTime()]);

  const reload = (data) => setTasks(data.tasks || []);

  const commit = async (id, payload) => {
    try { const { data } = await api.put(`/program/tasks/${id}`, payload); reload(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); load(); }
  };
  const addTask = async (parent_id = null) => {
    const start = fmt(new Date());
    const end = addDays(start, 2);
    try { const { data } = await api.post("/program/tasks", { name: parent_id ? "Nueva subtarea" : "Nueva tarea", start_date: start, end_date: end, parent_id }); reload(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("¿Eliminar esta tarea y sus subtareas?")) return;
    try { const { data } = await api.delete(`/program/tasks/${id}`); reload(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const move = async (id, direction) => {
    const fd = new FormData(); fd.append("direction", direction);
    try { const { data } = await api.post(`/program/tasks/${id}/move`, fd); reload(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggle = (t) => commit(t.id, { collapsed: !t.collapsed });

  const importFile = async (f) => {
    if (!f) return;
    if (tasks.length && !window.confirm("Esto REEMPLAZARÁ el cronograma actual con el del archivo subido. ¿Continuar?")) { if (fileRef.current) fileRef.current.value = ""; return; }
    setImporting(true);
    const fd = new FormData(); fd.append("file", f); fd.append("replace", "true");
    try { const { data } = await api.post("/program/tasks/import", fd); reload(data); toast.success("Cronograma importado. Ya puedes mover tiempos, editar o borrar tareas."); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const clearAll = async () => {
    if (!window.confirm("¿Borrar TODO el cronograma? Se eliminarán todas las tareas.")) return;
    try { const { data } = await api.delete("/program/tasks"); reload(data); toast.success("Cronograma borrado"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const downloadExcel = () => window.open(`${API}/program/tasks/excel`, "_blank");

  const commitPred = (t, value) => {
    const rows = String(value || "").split(/[,;\s]+/).map((x) => parseInt(x, 10)).filter((n) => !isNaN(n));
    const ids = rows.map((r) => rowToId[r]).filter((x) => x && x !== t.id);
    commit(t.id, { predecessors: [...new Set(ids)] });
  };

  if (loading) return <div className="text-muted-foreground p-6">Cargando cronograma…</div>;

  const visIndex = Object.fromEntries(visible.map((t, i) => [t.id, i]));
  const bodyH = Math.max(visible.length * ROW_H, ROW_H);

  return (
    <div>
      {canManage ? (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <button onClick={() => addTask(null)} data-testid="gantt-add-task" className="inline-flex items-center gap-1.5 h-9 px-3 bg-primary text-white rounded-sm font-semibold text-sm hover:bg-orange-600 transition-colors">
            <Plus size={16} weight="bold" /> Agregar tarea
          </button>
          <label className="inline-flex items-center gap-1.5 h-9 px-3 border border-input rounded-sm font-semibold text-sm cursor-pointer hover:bg-muted transition-colors" data-testid="gantt-import-label">
            <UploadSimple size={16} weight="bold" /> {importing ? "Importando…" : "Importar archivo (PDF/Project)"}
            <input ref={fileRef} type="file" className="hidden" onChange={(e) => importFile(e.target.files[0])} data-testid="gantt-import-input" disabled={importing} />
          </label>
          {tasks.length > 0 && (
            <button onClick={downloadExcel} data-testid="gantt-download-excel" className="inline-flex items-center gap-1.5 h-9 px-3 border border-input rounded-sm font-semibold text-sm hover:bg-muted transition-colors">
              <DownloadSimple size={16} weight="bold" /> Descargar Excel
            </button>
          )}
          {tasks.length > 0 && (
            <button onClick={clearAll} data-testid="gantt-clear-all" className="inline-flex items-center gap-1.5 h-9 px-3 border border-red-200 text-red-600 rounded-sm font-semibold text-sm hover:bg-red-50 transition-colors">
              <Trash size={16} weight="bold" /> Borrar todo
            </button>
          )}
          <span className="text-xs text-muted-foreground w-full sm:w-auto">Sube un PDF o archivo del cronograma y se convierte a este formato editable. Las <b>predecesoras</b> se escriben con el número de fila (ej. <code>3</code> o <code>3,5</code>); las fechas se ajustan solas. Las barras en <b className="text-red-600">rojo</b> son tareas <b>atrasadas</b> (pasó su término y no están al 100%).</span>
        </div>
      ) : (
        tasks.length > 0 && (
          <div className="flex items-center gap-2 mb-3">
            <button onClick={downloadExcel} data-testid="gantt-download-excel" className="inline-flex items-center gap-1.5 h-9 px-3 border border-input rounded-sm font-semibold text-sm hover:bg-muted transition-colors">
              <DownloadSimple size={16} weight="bold" /> Descargar Excel
            </button>
          </div>
        )
      )}

      {tasks.length === 0 ? (
        <div className="border border-dashed rounded-md p-10 text-center text-muted-foreground">
          Aún no hay tareas. {canManage && "Da clic en \u201cAgregar tarea\u201d para empezar tu cronograma."}
        </div>
      ) : (
        <div className="border border-border rounded-md overflow-hidden bg-card">
          <div className="flex">
            {/* Tabla */}
            <div className="flex-shrink-0" style={{ width: 620 }}>
              <div className="grid text-[11px] font-bold uppercase tracking-wide text-muted-foreground bg-muted/60 border-b border-border" style={{ gridTemplateColumns: "34px 1fr 96px 96px 56px 90px", height: HEAD_H, alignItems: "center" }}>
                <div className="px-1 text-center">#</div>
                <div className="px-2">Nombre</div>
                <div className="px-1">Inicio</div>
                <div className="px-1">Término</div>
                <div className="px-1 text-center">Días</div>
                <div className="px-1">Pred. / %</div>
              </div>
              {visible.map((t) => (
                <div key={t.id} data-testid={`task-row-${t.id}`} className={`grid items-center border-b border-border/70 ${t.is_group ? "bg-slate-50 font-semibold" : ""}`} style={{ gridTemplateColumns: "34px 1fr 96px 96px 56px 90px", height: ROW_H }}>
                  <div className="px-1 text-center text-xs text-muted-foreground">{t.row}</div>
                  <div className="px-2 flex items-center gap-1 min-w-0" style={{ paddingLeft: 8 + t.depth * 16 }}>
                    {t.is_group ? (
                      <button onClick={() => toggle(t)} data-testid={`task-toggle-${t.id}`} className="text-muted-foreground hover:text-foreground shrink-0">
                        {t.collapsed ? <CaretRight size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
                      </button>
                    ) : <span className="w-[14px] shrink-0" />}
                    <input defaultValue={t.name} key={`n-${t.id}-${t.name}`} disabled={!canManage} onBlur={(e) => e.target.value !== t.name && commit(t.id, { name: e.target.value })} onKeyDown={(e) => e.key === "Enter" && e.target.blur()} data-testid={`task-name-${t.id}`} className="w-full bg-transparent text-sm outline-none focus:bg-white focus:ring-1 focus:ring-primary/40 rounded px-1 truncate" />
                  </div>
                  <div className="px-0.5">
                    <input type="date" defaultValue={t.start_date || ""} key={`s-${t.id}-${t.start_date}`} disabled={!canManage || t.is_group} onChange={(e) => e.target.value && commit(t.id, { start_date: e.target.value, end_date: t.end_date })} data-testid={`task-start-${t.id}`} className="w-full bg-transparent text-[11px] outline-none focus:bg-white rounded px-0.5 disabled:opacity-70" />
                  </div>
                  <div className="px-0.5">
                    <input type="date" defaultValue={t.end_date || ""} key={`e-${t.id}-${t.end_date}`} disabled={!canManage || t.is_group} onChange={(e) => e.target.value && commit(t.id, { start_date: t.start_date, end_date: e.target.value })} data-testid={`task-end-${t.id}`} className="w-full bg-transparent text-[11px] outline-none focus:bg-white rounded px-0.5 disabled:opacity-70" />
                  </div>
                  <div className="px-1 text-center text-xs text-muted-foreground">{t.duration_days || "—"}</div>
                  <div className="px-1 flex items-center gap-1">
                    {t.is_group ? (
                      <span className="text-xs text-muted-foreground">{Math.round(t.progress || 0)}%</span>
                    ) : (
                      <>
                        <input defaultValue={(t.predecessors || []).map((id) => idToRow[id]).filter(Boolean).join(",")} key={`p-${t.id}-${(t.predecessors || []).join()}`} disabled={!canManage} onBlur={(e) => commitPred(t, e.target.value)} onKeyDown={(e) => e.key === "Enter" && e.target.blur()} placeholder="—" data-testid={`task-pred-${t.id}`} className="w-9 bg-transparent text-[11px] outline-none focus:bg-white rounded px-0.5 border border-transparent focus:border-primary/40" title="Predecesoras (número de fila)" />
                        <input type="number" min="0" max="100" defaultValue={Math.round(t.progress || 0)} key={`pr-${t.id}-${t.progress}`} disabled={!canManage} onBlur={(e) => commit(t.id, { progress: parseFloat(e.target.value || 0) })} onKeyDown={(e) => e.key === "Enter" && e.target.blur()} data-testid={`task-progress-${t.id}`} className="w-10 bg-transparent text-[11px] outline-none focus:bg-white rounded px-0.5" title="% avance" />
                      </>
                    )}
                    {canManage && (
                      <span className="flex items-center opacity-60 hover:opacity-100 ml-auto">
                        <button onClick={() => move(t.id, "up")} title="Subir" className="text-muted-foreground hover:text-foreground"><ArrowUp size={12} weight="bold" /></button>
                        <button onClick={() => move(t.id, "down")} title="Bajar" className="text-muted-foreground hover:text-foreground"><ArrowDown size={12} weight="bold" /></button>
                        <button onClick={() => addTask(t.id)} title="Agregar subtarea" className="text-primary hover:text-orange-600"><FolderSimplePlus size={13} weight="bold" /></button>
                        <button onClick={() => del(t.id)} title="Eliminar" className="text-muted-foreground hover:text-danger"><Trash size={12} weight="bold" /></button>
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Timeline */}
            <div ref={timelineRef} className="flex-1 overflow-x-auto border-l border-border">
              <div style={{ width: timelineW, position: "relative" }}>
                {/* Encabezado meses */}
                <div className="bg-muted/60 border-b border-border relative" style={{ height: HEAD_H }}>
                  {months.map((m, idx) => {
                    const left = Math.round((m - minMonth) / DAY) * PX_DAY;
                    return (
                      <div key={idx} className="absolute top-0 bottom-0 border-l border-border/60 text-[11px] font-semibold text-muted-foreground px-1 pt-1" style={{ left }}>
                        {m.toLocaleDateString("es-MX", { month: "short", year: "2-digit" })}
                      </div>
                    );
                  })}
                </div>
                {/* Cuerpo */}
                <div className="relative" style={{ height: bodyH }}>
                  {/* Líneas verticales por mes */}
                  {months.map((m, idx) => {
                    const left = Math.round((m - minMonth) / DAY) * PX_DAY;
                    return <div key={idx} className="absolute top-0 bottom-0 w-px bg-border/50" style={{ left }} />;
                  })}
                  {/* Línea de hoy */}
                  {(() => {
                    const t = new Date(); const off = Math.round((t - minMonth) / DAY);
                    if (off < 0 || off > totalDays) return null;
                    return <div className="absolute top-0 bottom-0 w-px bg-red-500/70 z-20" style={{ left: off * PX_DAY }} title="Hoy" />;
                  })()}
                  {/* Flechas de dependencia */}
                  <svg className="absolute inset-0 pointer-events-none" width={timelineW} height={bodyH} style={{ zIndex: 15 }}>
                    <defs>
                      <marker id="ah" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                        <path d="M0,0 L6,3 L0,6 Z" fill="#64748b" />
                      </marker>
                    </defs>
                    {visible.map((t) => (t.predecessors || []).map((pid) => {
                      if (!(pid in visIndex) || !(t.id in visIndex)) return null;
                      const p = tasks.find((x) => x.id === pid);
                      if (!p || !p.end_date || !t.start_date) return null;
                      const x1 = (dayOffset(p.end_date) + 1) * PX_DAY;
                      const y1 = visIndex[pid] * ROW_H + ROW_H / 2;
                      const x2 = dayOffset(t.start_date) * PX_DAY;
                      const y2 = visIndex[t.id] * ROW_H + ROW_H / 2;
                      const midX = Math.max(x1 + 8, x2 - 8);
                      const d = `M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${y2} L ${x2} ${y2}`;
                      return <path key={`${pid}-${t.id}`} d={d} fill="none" stroke="#64748b" strokeWidth="1.2" markerEnd="url(#ah)" />;
                    }))}
                  </svg>
                  {/* Barras */}
                  {visible.map((t, i) => {
                    if (!t.start_date || !t.end_date) return null;
                    const left = dayOffset(t.start_date) * PX_DAY;
                    const w = Math.max(PX_DAY, ((_dur(t)) * PX_DAY));
                    const top = i * ROW_H + 7;
                    if (t.is_group) {
                      return (
                        <div key={t.id} data-testid={`bar-${t.id}`} className="absolute bg-slate-800 rounded-sm" style={{ left, width: w, top: top + 4, height: 8 }} title={`${t.name}`}>
                          <span className="absolute -left-1 -top-1 w-0 h-0 border-l-4 border-r-4 border-t-[6px] border-l-transparent border-r-transparent border-t-slate-800" />
                          <span className="absolute -right-1 -top-1 w-0 h-0 border-l-4 border-r-4 border-t-[6px] border-l-transparent border-r-transparent border-t-slate-800" />
                        </div>
                      );
                    }
                    const today0 = new Date(); today0.setHours(0, 0, 0, 0);
                    const overdue = parse(t.end_date) < today0 && (t.progress || 0) < 100;
                    return (
                      <div key={t.id} data-testid={`bar-${t.id}`} className={`absolute rounded-sm overflow-hidden ${overdue ? "bg-red-200" : "bg-primary/25"}`} style={{ left, width: w, top, height: ROW_H - 14 }} title={`${t.name} · ${t.start_date} → ${t.end_date}${overdue ? " · ATRASADA" : ""}`}>
                        <div className={`h-full flex items-center ${overdue ? "bg-red-500" : "bg-primary"}`} style={{ width: `${Math.min(100, t.progress || 0)}%` }} />
                        <span className="absolute inset-y-0 left-1 flex items-center text-[10px] text-slate-700 font-semibold whitespace-nowrap">{Math.round(t.progress || 0)}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function _dur(t) {
  if (t.duration_days) return t.duration_days;
  if (t.start_date && t.end_date) return Math.max(1, Math.round((parse(t.end_date) - parse(t.start_date)) / DAY) + 1);
  return 1;
}
